import { composeRewrites, createRouter } from '@tanstack/react-router';
import type {
  AnyRouter,
  NavigateOptions,
  RegisteredRouter,
} from '@tanstack/react-router';
import { optional } from 'inversify';
import { inject, Service } from '@piwikpro/platform';
import {
  ADMINISTRATION_OVERLAY_SEARCH_PARAM,
  AdministrationOverlaySuffix,
  administrationOverlayRewrite,
  isAdministrationOverlayPath,
} from './administrationOverlayRewrite';
import {
  SUPPORT_PANEL_OVERLAY_SEARCH_PARAM,
  SupportPanelOverlaySuffix,
  isSupportPanelOverlayPath,
  supportPanelOverlayRewrite,
} from './supportPanelOverlayRewrite';

type HistoryPushOptions = { state?: any, replace?: boolean };

/**
 * Same inference as TanStack `router.navigate` / `useNavigate()`.
 * `TTo` must stay a free type parameter — if it defaults (e.g. to `'.'`),
 * `to` is not checked against the registered route tree.
 */
type RouterPushOptions<
  TRouter extends RegisteredRouter,
  TTo extends string | undefined,
  TFrom extends string,
  TMaskFrom extends string,
  TMaskTo extends string,
> = NavigateOptions<TRouter, TFrom, TTo, TMaskFrom, TMaskTo>;

/** Implementation-only: do not instantiate NavigateOptions here (huge/fragile). */
type PushInput = string | {
  to?: unknown
  replace?: boolean
  state?: any
  search?: unknown
};

/** App crates bind their generated route tree under this token. */
export const ROUTER_ROUTE_TREE = 'RouterCrate.routeTree';

const locationRewrite = composeRewrites([
  administrationOverlayRewrite,
  supportPanelOverlayRewrite,
]);

/**
 * RouterService - Singleton facade over TanStack Router.
 * Creates the router from DI-bound routeTree (+ optional history).
 */
@Service()
export class RouterService {
  private readonly router: AnyRouter | null;

  constructor(
    @inject(ROUTER_ROUTE_TREE) @optional() routeTree?: any,
  ) {
    this.router = routeTree
      ? createRouter({
        routeTree,
        rewrite: locationRewrite,
      })
      : null;
  }

  /**
   * Get the current router instance
   */
  getRouter(): AnyRouter {
    if (!this.router) {
      throw new Error(
        'Router not initialized. Bind RouterCrate.routeTree before resolving RouterCrate.router.',
      );
    }
    return this.router;
  }

  /**
   * Get the current location
   */
  get location() {
    return this.getRouter().state.location;
  }

  /**
   * Get the current pathname
   */
  get pathname(): string {
    return this.location.pathname;
  }

  /**
   * Get the current search params
   */
  get search(): Record<string, any> {
    return this.location.search || {};
  }

  /**
   * Get the history instance
   */
  get history() {
    return this.getRouter().history;
  }

  /**
   * Navigate to a new location (programmatic navigation)
   * @param to - Route pattern + params, or a raw path string (escape hatch)
   * @param options - Navigation options
   */
  /* eslint-disable no-dupe-class-members, lines-between-class-members -- TS method overloads */
  push(
    to: string,
    options?: HistoryPushOptions,
  ): void;
  push<
    TRouter extends RegisteredRouter,
    TTo extends string | undefined,
    TFrom extends string = string,
    TMaskFrom extends string = TFrom,
    TMaskTo extends string = '',
  >(
    to: RouterPushOptions<TRouter, TTo, TFrom, TMaskFrom, TMaskTo>,
    options?: HistoryPushOptions,
  ): void;
  push(
    to: PushInput,
    options?: HistoryPushOptions,
  ): void {
    this.executePush(to, options);
  }
  /* eslint-enable no-dupe-class-members, lines-between-class-members */

  private executePush(
    to: PushInput,
    options?: HistoryPushOptions,
  ): void {
    const path = this.resolvePushPath(to);
    const replace = (typeof to === 'object' && to.replace) || options?.replace || false;
    const state = (typeof to === 'object' && to.state) || options?.state;
    // Legacy callers pass "?query" in the path string (react-router history.push style).
    // TanStack's `navigate({ to })` treats "?" as part of the pathname, so use history API instead.
    if (path && (path.includes('?') || path.includes('#'))) {
      if (replace) {
        this.history.replace(path, state);
      } else {
        this.history.push(path, state);
      }
      return;
    }

    if (typeof to !== 'string') {
      this.getRouter().navigate({
        ...to,
        replace: to.replace ?? replace,
        state: to.state ?? state,
        ...(path
          && !isAdministrationOverlayPath(path)
          && !isSupportPanelOverlayPath(path)
          && path !== this.pathname
          && !to.search
          ? {
            search: (prev: Record<string, unknown>) => {
              const next = { ...(prev || {}) };
              delete next[ADMINISTRATION_OVERLAY_SEARCH_PARAM];
              delete next[SUPPORT_PANEL_OVERLAY_SEARCH_PARAM];
              return next;
            },
          }
          : {}),
      } as any);
      return;
    }

    this.getRouter().navigate({
      to: path,
      replace,
      state,
      // Keep overlay when staying on the same base path (e.g. pagination after a
      // deep-linked `/o/add-users`). Clear only when leaving that path, so output
      // does not re-append `/m/create` onto the next destination.
      ...(isAdministrationOverlayPath(to)
        || isSupportPanelOverlayPath(to)
        || to === this.pathname
        ? {}
        : {
          search: (prev: Record<string, unknown>) => {
            const next = { ...(prev || {}) };
            delete next[ADMINISTRATION_OVERLAY_SEARCH_PARAM];
            delete next[SUPPORT_PANEL_OVERLAY_SEARCH_PARAM];
            return next;
          },
        }),
    } as any);
  }

  private resolvePushPath(to: PushInput): string | undefined {
    if (typeof to === 'string') {
      return to;
    }

    return typeof to.to === 'string' ? to.to : undefined;
  }

  /**
   * Open a list/details overlay while staying on the current base path.
   * Public URL gets the suffix via rewrite output.
   */
  openAdministrationOverlay(
    suffix: AdministrationOverlaySuffix,
    options?: { state?: any, replace?: boolean },
  ): void {
    const overlay = suffix.replace(/^\//, '');

    this.getRouter().navigate({
      to: this.pathname,
      replace: options?.replace || false,
      state: options?.state,
      search: (prev: Record<string, unknown>) => ({
        ...(prev || {}),
        [ADMINISTRATION_OVERLAY_SEARCH_PARAM]: overlay,
      }),
    } as any);
  }

  /**
   * Close a list-level create/invite overlay while staying on the current base path.
   * Clears the rewrite search flag so output no longer re-appends the public suffix.
   */
  dismissAdministrationOverlay(): void {
    this.getRouter().navigate({
      to: this.pathname,
      replace: true,
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };
        delete next[ADMINISTRATION_OVERLAY_SEARCH_PARAM];
        return next;
      },
    } as any);
  }

  /**
   * Open a support-panel overlay while staying on the current base path.
   * Public URL gets the suffix via rewrite output.
   */
  openSupportPanelOverlay(
    suffix: SupportPanelOverlaySuffix,
    options?: { state?: any },
  ): void {
    const overlay = suffix.replace(/^\//, '');

    this.getRouter().navigate({
      to: this.pathname,
      state: options?.state,
      search: (prev: Record<string, unknown>) => ({
        ...(prev || {}),
        [SUPPORT_PANEL_OVERLAY_SEARCH_PARAM]: overlay,
      }),
    } as any);
  }

  /**
   * Close a support-panel overlay while staying on the current base path.
   * Clears the rewrite search flag so output no longer re-appends the public suffix.
   */
  dismissSupportPanelOverlay(): void {
    this.getRouter().navigate({
      to: this.pathname,
      replace: true,
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };
        delete next[SUPPORT_PANEL_OVERLAY_SEARCH_PARAM];
        return next;
      },
    } as any);
  }

  /**
   * Replace the current location
   * @param to - The path to navigate to
   * @param options - Navigation options
   */
  replace(to: string, options?: { state?: any }): void {
    this.push(to, { ...options, replace: true });
  }

  /**
   * Go back in history
   */
  back(): void {
    this.history.back();
  }

  /**
   * Go forward in history
   */
  forward(): void {
    this.history.forward();
  }

  /**
   * Go to a specific point in history
   * @param delta - Number of steps to go (negative for back, positive for forward)
   */
  go(delta: number): void {
    this.history.go(delta);
  }

  /**
   * Get route parameters
   * Use this in components to access route params
   */
  useParams<T = Record<string, string>>(): T {
    // This will be used via hooks in components
    const router = this.getRouter();
    return (router.state.matches[router.state.matches.length - 1]?.params || {}) as T;
  }

  /**
   * Alias for useParams - for consistency with old API
   */
  getParams<T = Record<string, string>>(): T {
    return this.useParams<T>();
  }

  /**
   * Alias for push - for consistency with TanStack Router API
   */
  /* eslint-disable no-dupe-class-members, lines-between-class-members -- TS method overloads */
  navigate(
    to: string,
    options?: HistoryPushOptions,
  ): void;
  navigate<
    TRouter extends RegisteredRouter,
    TTo extends string | undefined,
    TFrom extends string = string,
    TMaskFrom extends string = TFrom,
    TMaskTo extends string = '',
  >(
    to: RouterPushOptions<TRouter, TTo, TFrom, TMaskFrom, TMaskTo>,
    options?: HistoryPushOptions,
  ): void;
  navigate(
    to: PushInput,
    options?: HistoryPushOptions,
  ): void {
    this.executePush(to, options);
  }
  /* eslint-enable no-dupe-class-members, lines-between-class-members */

  /**
   * Get current path - for consistency with old API
   */
  getCurrentPath(): string {
    return this.pathname;
  }

  /**
   * Check if we're currently at a specific path
   * @param path - The path to check
   */
  isActive(path: string): boolean {
    return this.pathname === path || this.pathname.startsWith(path);
  }

  /**
   * Build a URL with search params
   * @param path - Base path
   * @param params - Search parameters
   */
  buildUrl(path: string, params?: Record<string, any>): string {
    if (!params || Object.keys(params).length === 0) {
      return path;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${path}?${queryString}` : path;
  }

  /**
   * Parse search params from current location
   */
  getSearchParams(): URLSearchParams {
    return new URLSearchParams(this.location.searchStr || '');
  }

  /**
   * Get a specific search param value
   * @param key - The param key
   */
  getSearchParam(key: string): string | null {
    return this.getSearchParams().get(key);
  }
}
